import React, { ReactElement, ReactNode } from 'react';
import { KeyboardAvoidingView, View } from 'react-native';
import { Stack } from '@/common/components/Stack/Stack';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { useKeyboard } from '@/common/hooks/useKeyboard';
import { tv } from '@/common/theme/tv.config';

// Define the Primary and Secondary components first
export type PrimaryProps = { children: ReactNode };
export type SecondaryProps = { children: ReactNode };

const Primary = ({ children }: PrimaryProps): ReactElement => {
  return <>{children}</>;
};
Primary.displayName = 'SplitLayout.Primary';

const Secondary = ({ children }: SecondaryProps): ReactElement => {
  return <>{children}</>;
};
Secondary.displayName = 'SplitLayout.Secondary';

type SplitLayoutChild = ReactElement<PrimaryProps | SecondaryProps>;

export type TSplitLayoutProps = {
  children: SplitLayoutChild | SplitLayoutChild[];
};

const RootSplitLayout = ({ children }: TSplitLayoutProps) => {
  const matches = useBreakpoints(['lg']);

  let primaryContent;
  let secondaryContent;

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === Primary) {
        primaryContent = child.props.children;
      } else if (child.type === Secondary) {
        secondaryContent = child.props.children;
      }
    }
  });

  return matches.lg ? (
    <RowLayout
      primaryContent={primaryContent}
      secondaryContent={secondaryContent}
    />
  ) : (
    <ColumnLayout
      primaryContent={primaryContent}
      secondaryContent={secondaryContent}
    />
  );
};

type TLayoutProps = {
  primaryContent?: ReactNode;
  secondaryContent?: ReactNode;
};

const ColumnLayout = ({ primaryContent, secondaryContent }: TLayoutProps) => {
  const { isKeyboardVisible } = useKeyboard();

  return (
    <KeyboardAvoidingView behavior="padding" className={columnStyles.root}>
      <Stack
        grow
        className={columnStyles.container({
          keyboardVisible: isKeyboardVisible,
        })}
      >
        {secondaryContent}
        {primaryContent}
      </Stack>
    </KeyboardAvoidingView>
  );
};

const RowLayout = ({ primaryContent, secondaryContent }: TLayoutProps) => {
  return (
    <View className={rowStyles.root}>
      <View className={rowStyles.leftPanel}>{primaryContent}</View>
      <View className={rowStyles.rightPanel}>{secondaryContent}</View>
    </View>
  );
};

export const SplitLayout = {
  Root: RootSplitLayout,
  Primary,
  Secondary,
};

export const columnStyles = {
  root: 'flex-1',
  container: tv({
    base: 'pb-safe bg-neutral20',
    variants: {
      keyboardVisible: {
        true: 'pb-0',
      },
    },
  }),
};

export const rowStyles = {
  root: 'relative w-full min-h-screen flex flex-row h-screen',
  leftPanel: 'w-1/2',
  rightPanel: 'w-1/2',
};
